import React, { useState, useEffect } from 'react';
import './ProductTour.css';

const ProductTour = ({ translate }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isActive, setIsActive] = useState(false);

  const tourSteps = [
    {
      target: '.start-new-btn',
      title: translate('New Chat'),
      description: translate('Start a fresh conversation with the chatbot'),
      position: 'right'
    },
    {
      target: '.history-container',
      title: translate('Chat History'),
      description: translate('Access your previous conversations'),
      position: 'right'
    },
    {
      target: '.start-new-btn:last-of-type',
      title: translate('Submit Feedback'),
      description: translate('Share your experience and help us improve'),
      position: 'right'
    },
    {
      target: '.toggle-sidebar',
      title: translate('Toggle Sidebar'),
      description: translate('Collapse or expand the sidebar for more space'),
      position: 'right'
    },
    {
      target: '.custom-dropdown-language',
      title: translate('Language Selection'),
      description: translate('Choose your preferred language'),
      position: 'bottom'
    },
    {
      target: '.custom-dropdown',
      title: translate('User Profile'),
      description: translate('Access your profile and logout options'),
      position: 'bottom'
    },
    {
      target: '.model-dropdown',
      title: translate('Model Selection'),
      description: translate('Select the vehicle model for your conversation'),
      position: 'top'
    },
    {
      target: '.input-question',
      title: translate('Ask Questions'),
      description: translate('Type your questions here and get instant responses'),
      position: 'top'
    }
  ];

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      // 1. Check Local Storage first (Optimization)
      if (localStorage.getItem('productTourCompleted')) {
        return;
      }
      try {
        const email = localStorage.getItem('userinfo');
        console.log("ProductTour: Checking status for email:", email); // DEBUG

        if (email) {
          const response = await fetch(`/onboarding/status?email=${encodeURIComponent(email)}`);
          console.log("ProductTour: API Response status:", response.status); // DEBUG

          if (response.ok) {
            const data = await response.json();
            console.log("ProductTour: API Data:", data); // DEBUG

            if (data.is_onboarded) {
              console.log("ProductTour: User is onboarded. Skipping tour."); // DEBUG
              localStorage.setItem('productTourCompleted', 'true');
              return;
            } else {
              // Valid response, but not onboarded -> Start tour
              console.log("ProductTour: User not onboarded. Starting tour."); // DEBUG
              setTimeout(() => setIsActive(true), 1000);
            }
          } else {
            // API failed -> Fallback to starting tour
            console.log("ProductTour: API Error. Starting tour."); // DEBUG
            setTimeout(() => setIsActive(true), 1000);
          }
        } else {
          console.log("ProductTour: No email found in sessionStorage. Starting tour."); // DEBUG
          setTimeout(() => setIsActive(true), 1000);
        }
      } catch (error) {
        console.error("Failed to check onboarding status:", error);
        // Error -> Fallback to starting tour
        setTimeout(() => setIsActive(true), 1000);
      }
    };

    checkOnboardingStatus();
  }, []);

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    completeTour();
  };

  const completeTour = async () => {
    localStorage.setItem('productTourCompleted', 'true');
    setIsActive(false);

    // Update Database
    try {
      const email = localStorage.getItem('userinfo');
      if (email) {
        console.log("ProductTour: Firing /onboarding/complete for", email);
        const response = await fetch('/onboarding/complete', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ user_email: email }),
        });

        if (response.ok) {
          console.log("ProductTour: Successfully persisted completion to server.");
        } else {
          console.error("ProductTour: Failed to persist completion to server. Status:", response.status);
        }
      } else {
        console.log("ProductTour: No email in session, skipped saving to server.");
      }
    } catch (error) {
      console.error("Failed to complete onboarding on server:", error);
    }
  };

  const getTargetPosition = () => {
    const step = tourSteps[currentStep];
    const targetElement = document.querySelector(step.target);

    if (!targetElement) return null;

    const rect = targetElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    // Calculate spotlight position (padding for visual breathing room)
    const padding = 10;
    const spotlightTop = rect.top + scrollTop - padding;
    const spotlightLeft = rect.left + scrollLeft - padding;
    const spotlightWidth = rect.width + (padding * 2);
    const spotlightHeight = rect.height + (padding * 2);

    // Calculate tooltip position with collision detection
    let tooltipTop = 0;
    let tooltipLeft = 0;

    // Default offset from element
    const tooltipOffset = 20;
    const tooltipWidth = 320; // Estimated width of tooltip from CSS
    const minTooltipHeight = 350; // Use a safe upper bound for height to prevent cutoff

    // Determine strict placement or auto check
    let position = step.position;

    // Edge detection
    const isRightEdge = rect.right + tooltipWidth + tooltipOffset > window.innerWidth;
    const isLeftEdge = rect.left - tooltipWidth - tooltipOffset < 0;
    const isBottomEdge = rect.bottom + minTooltipHeight + tooltipOffset > window.innerHeight; // Approx tooltip height
    const isTopEdge = rect.top - minTooltipHeight - tooltipOffset < 0;

    // Auto-adjust 'right' to 'left' if valid
    if (position === 'right' && isRightEdge) {
      position = 'left';
    }
    // Auto-adjust 'bottom' to 'top' if valid
    else if (position === 'bottom' && isBottomEdge && !isTopEdge) { // Only flip if top is available
      position = 'top';
    }
    // Auto-adjust 'top' to 'bottom' if valid (rare, usually header items)
    else if (position === 'top' && isTopEdge && !isBottomEdge) { // Only flip if bottom is available
      position = 'bottom';
    }
    // If 'left' and it goes off screen, try 'right'
    else if (position === 'left' && isLeftEdge && !isRightEdge) {
      position = 'right';
    }


    // Calculate tooltip style
    let tooltipStyle = {};

    switch (position) {
      case 'right':
        // Default: Vertically centered
        tooltipStyle = {
          top: rect.top + scrollTop + (rect.height / 2),
          left: rect.right + scrollLeft + tooltipOffset,
          transform: 'translateY(-50%)'
        };

        // Edge check: Top
        if (rect.top - (minTooltipHeight / 2) < 0) {
          tooltipStyle = {
            top: scrollTop + 10,
            left: rect.right + scrollLeft + tooltipOffset,
            transform: 'none'
          };
        }
        // Edge check: Bottom
        else if (rect.bottom + (minTooltipHeight / 2) > window.innerHeight) {
          tooltipStyle = {
            top: 'auto',
            bottom: 10,
            left: rect.right + scrollLeft + tooltipOffset,
            transform: 'none'
          };
        }
        break;

      case 'left':
        // Default: Vertically centered
        tooltipStyle = {
          top: rect.top + scrollTop + (rect.height / 2),
          left: rect.left + scrollLeft - tooltipWidth - tooltipOffset,
          transform: 'translateY(-50%)'
        };

        // Edge check: Top
        if (rect.top - (minTooltipHeight / 2) < 0) {
          tooltipStyle = {
            top: scrollTop + 10,
            left: rect.left + scrollLeft - tooltipWidth - tooltipOffset,
            transform: 'none'
          };
        }
        // Edge check: Bottom
        else if (rect.bottom + (minTooltipHeight / 2) > window.innerHeight) {
          tooltipStyle = {
            top: 'auto',
            bottom: 10,
            left: rect.left + scrollLeft - tooltipWidth - tooltipOffset,
            transform: 'none'
          };
        }
        break;

      case 'bottom':
        // Default: Horizontally centered
        tooltipStyle = {
          top: rect.bottom + scrollTop + tooltipOffset,
          left: rect.left + scrollLeft + (rect.width / 2),
          transform: 'translateX(-50%)'
        };

        // Edge check: Left
        if (rect.left - (tooltipWidth / 2) < 0) {
          tooltipStyle = {
            top: rect.bottom + scrollTop + tooltipOffset,
            left: scrollLeft + 10,
            transform: 'none'
          };
        }
        // Edge check: Right
        else if (rect.right + (tooltipWidth / 2) > window.innerWidth) {
          tooltipStyle = {
            top: rect.bottom + scrollTop + tooltipOffset,
            left: 'auto',
            right: 10,
            transform: 'none'
          };
        }
        break;

      case 'top':
        // Default: Horizontally centered
        tooltipStyle = {
          bottom: window.innerHeight - (rect.top + scrollTop) + tooltipOffset,
          left: rect.left + scrollLeft + (rect.width / 2),
          transform: 'translateX(-50%)'
        };

        // Edge check: Left
        if (rect.left - (tooltipWidth / 2) < 0) {
          tooltipStyle = {
            bottom: window.innerHeight - (rect.top + scrollTop) + tooltipOffset,
            left: scrollLeft + 10,
            transform: 'none'
          };
        }
        // Edge check: Right
        else if (rect.right + (tooltipWidth / 2) > window.innerWidth) {
          tooltipStyle = {
            bottom: window.innerHeight - (rect.top + scrollTop) + tooltipOffset,
            left: 'auto',
            right: 10,
            transform: 'none'
          };
        }
        break;
      default:
        break;
    }

    return {
      spotlight: { top: spotlightTop, left: spotlightLeft, width: spotlightWidth, height: spotlightHeight },
      tooltip: tooltipStyle
    };
  };

  const getTooltipPosition = (targetPos) => {
    if (!targetPos) return {};
    return targetPos.tooltip;
  };

  if (!isActive) return null;

  const targetPos = getTargetPosition();
  // If element is not found, don't render
  if (!targetPos) return null;

  const tooltipStyle = getTooltipPosition(targetPos);
  const step = tourSteps[currentStep];

  return (
    <>
      {/* Overlay */}
      <div className="tour-overlay" onClick={handleSkip} />

      {/* Spotlight */}
      {targetPos && (
        <div
          className="tour-spotlight"
          style={{
            top: targetPos.spotlight.top,
            left: targetPos.spotlight.left,
            width: targetPos.spotlight.width,
            height: targetPos.spotlight.height
          }}
        />
      )}

      {/* Tooltip */}
      <div className="tour-tooltip" style={tooltipStyle}>
        <div className="tour-tooltip-header">
          <h3 className="tour-tooltip-title">{step.title}</h3>
          <button className="tour-skip-btn" onClick={handleSkip}>×</button>
        </div>

        <p className="tour-tooltip-description">
          {step.description}
        </p>

        <div className="tour-tooltip-footer">
          <div className="tour-progress">
            {currentStep + 1} / {tourSteps.length}
          </div>

          <div className="tour-buttons">
            {currentStep > 0 && (
              <button className="tour-btn tour-btn-secondary" onClick={handlePrevious}>
                {translate('Previous')}
              </button>
            )}

            <button className="tour-btn tour-btn-primary" onClick={handleNext}>
              {currentStep === tourSteps.length - 1 ? translate('Finish') : translate('Next')}
            </button>
          </div>
        </div>
      </div>

      {/* Progress dots */}
      <div className="tour-progress-dots">
        {tourSteps.map((_, index) => (
          <div
            key={index}
            className={`tour-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
            onClick={() => setCurrentStep(index)}
          />
        ))}
      </div>
    </>
  );
};

export default ProductTour;
